import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { monitorAPI, notificationsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import ApiHealthCheck from '../components/Common/ApiHealthCheck';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';

const formatRoleLabel = (role?: string | null): string => {
  if (!role) return 'Unknown';
  return String(role)
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data, isLoading, refetch } = useQuery('command-center', monitorAPI.getCommandCenter, {
    refetchInterval: 30000,
  });

  const { data: notificationsData } = useQuery('dashboard-notifications', () => notificationsAPI.list({ limit: 5 }), {
    refetchInterval: 30000,
  });

  const handleStartMonitor = async () => {
    try {
      await monitorAPI.startMonitor();
      toast.success('Monitor started successfully');
      refetch();
    } catch {
      toast.error('Failed to start monitor');
    }
  };

  const handleStopMonitor = async () => {
    try {
      await monitorAPI.stopMonitor();
      toast.success('Monitor stopped');
      refetch();
    } catch {
      toast.error('Failed to stop monitor');
    }
  };

  const starlinkStatus = data?.monitorStatuses?.find((status: any) => status.service === 'starlink');
  const topUsage = data?.topUsage || [];
  const staleForms = data?.staleForms || [];
  const actionQueue = data?.actionQueue || { nearCap: 0, atCap: 0, failedMonitors: 0, disabledIntegrations: 0 };
  const notifications = notificationsData?.data || [];
  const canControlMonitor = Boolean(user?.isSuperAdmin && !user?.hasAuditAccess);

  const heroStats = [
    { icon: 'fas fa-user-shield', label: `Role: ${formatRoleLabel(user?.role)}` },
    { icon: 'fas fa-bell', label: `${data?.unreadNotifications || 0} unread alerts` },
    { icon: 'fas fa-clock', label: `Updated ${new Date().toLocaleTimeString()}` },
  ];

  const quickLinks = [
    { label: 'Pool customers', path: '/forms', icon: 'fas fa-file-alt' },
    { label: '100% usage', path: '/usage-100-percent', icon: 'fas fa-exclamation-triangle' },
    { label: 'Power BI', path: '/powerbi', icon: 'fas fa-chart-bar' },
    { label: 'Users', path: '/users', icon: 'fas fa-users-cog' },
  ];

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: starlinkStatus?.running ? 'Monitor running' : 'Monitor needs attention', tone: starlinkStatus?.running ? 'green' : 'amber' }}
        title={`Welcome back${user?.username ? `, ${user.username}` : ''}.`}
        description="Operations command center for usage risk, monitor health, disabled integrations, and the next customer actions that need attention."
        stats={heroStats}
        actions={
          <>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur">
              <ApiHealthCheck />
            </div>
            <button
              onClick={canControlMonitor ? (starlinkStatus?.running ? handleStopMonitor : handleStartMonitor) : undefined}
              disabled={!canControlMonitor}
              className={`rounded-2xl px-5 py-3 font-semibold transition ${
                canControlMonitor
                  ? starlinkStatus?.running
                    ? 'bg-rose-500 text-white hover:bg-rose-600'
                    : 'bg-white text-slate-900 hover:bg-slate-100'
                  : 'cursor-not-allowed bg-white/10 text-white/60'
              }`}
            >
              {starlinkStatus?.running ? 'Stop monitor' : 'Start monitor'}
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Unread alerts" value={data?.unreadNotifications || 0} hint="Persisted backend notifications" icon="fas fa-bell" accent="blue" />
        <MetricCard label="Near cap" value={actionQueue.nearCap} hint="Service lines at or above 75%" icon="fas fa-gauge-high" accent="amber" />
        <MetricCard label="At cap" value={actionQueue.atCap} hint="100%+ usage needing action" icon="fas fa-triangle-exclamation" accent="red" />
        <MetricCard label="Failed or disabled" value={(actionQueue.failedMonitors || 0) + (actionQueue.disabledIntegrations || 0)} hint="Monitor and integration attention points" icon="fas fa-heart-pulse" accent="violet" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-900">Role-focused quick actions</h2>
              <p className="mt-1 text-sm text-gray-500">
                {user?.hasAuditAccess
                  ? 'Read-only access to audits, alerts, and system health.'
                  : user?.role === 'account_manager'
                    ? 'Assigned customer risk and customer-facing actions.'
                    : 'Operations, integrations, and identity management.'}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {quickLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="rounded-2xl border border-gray-200 p-5 text-left transition hover:border-blue-200 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                    <i className={link.icon}></i>
                  </div>
                  <i className="fas fa-arrow-right text-gray-300"></i>
                </div>
                <div className="mt-4 font-bold text-gray-900">{link.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-gray-900">Operational inbox</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {notifications.length} recent
            </span>
          </div>
          <div className="mt-5 space-y-3">
            {notifications.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No persisted notifications yet.
              </div>
            ) : (
              notifications.map((notification: any) => (
                <button
                  key={notification.id}
                  onClick={() => notification.targetUrl && navigate(notification.targetUrl)}
                  className="w-full rounded-2xl border border-gray-100 p-4 text-left transition hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900">{notification.title}</div>
                      <div className="mt-1 text-sm text-gray-600">{notification.body}</div>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase text-gray-500">
                      {notification.severity}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">{new Date(notification.createdAt).toLocaleString()}</div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-gray-900">Monitor health</h2>
            <button onClick={() => refetch()} className="text-sm font-semibold text-blue-700 hover:text-blue-900">
              Refresh
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {isLoading ? (
              <div className="text-sm text-gray-500">Loading command center...</div>
            ) : (
              (data?.monitorStatuses || []).map((status: any) => (
                <div key={status.service} className="rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold capitalize text-gray-900">{status.service.replace('-', ' ')}</div>
                      <div className="mt-1 text-sm text-gray-600">{status.message || 'No status message yet'}</div>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-700">
                      {status.state}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Last success: {status.lastSuccessAt ? new Date(status.lastSuccessAt).toLocaleString() : 'Never'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-gray-900">Top usage</h2>
            <button onClick={() => navigate('/forms')} className="text-sm font-semibold text-blue-700 hover:text-blue-900">
              View all
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {topUsage.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No service lines available yet.
              </div>
            ) : (
              topUsage.map((form: any) => (
                <button
                  key={form.id}
                  onClick={() => navigate(`/forms/${form.id}/workspace`)}
                  className="w-full rounded-2xl border border-gray-100 p-4 text-left transition hover:shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900">{form.customerName}</div>
                      <div className="text-sm text-gray-600">{form.serviceLine}</div>
                    </div>
                    <div className="text-sm font-bold text-gray-900">{Number(form.usagePercentage || 0).toFixed(1)}%</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-gray-900">Stale forms</h2>
          <span className="text-sm text-gray-500">Not checked in the last 24 hours</span>
        </div>
        <div className="mt-5 space-y-3">
          {staleForms.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
              No stale forms right now.
            </div>
          ) : (
            staleForms.map((form: any) => (
              <button
                key={form.id}
                onClick={() => navigate(`/forms/${form.id}/workspace`)}
                className="flex w-full items-center justify-between rounded-2xl border border-gray-100 p-4 text-left transition hover:shadow-sm"
              >
                <div>
                  <div className="font-semibold text-gray-900">{form.customerName}</div>
                  <div className="text-sm text-gray-600">{form.serviceLine}</div>
                </div>
                <div className="text-xs text-gray-400">{form.lastChecked ? new Date(form.lastChecked).toLocaleString() : 'Never'}</div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
